import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Card,
  CardContent,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  CalendarToday as CalendarIcon,
  Event as EventIcon,
  Hotel as HotelIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  NightsStay as NightsStayIcon,
  Cancel as CancelIcon,
  Warning as NoShowIcon,
  FactCheck as FactCheckIcon,
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { getBookingDetails, markBookingNoShow } from '../../services/api';
import api from '../../services/api';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { resolveStayCheckInDisplay, resolveStayCheckOutDisplay, resolveActualCheckInDisplay, resolveActualCheckOutDisplay, isSameDayStay } from '../../utils/bookingStayDisplay';
import ActualStayDateChips from './ActualStayDateChips';
import StayDurationChip from './StayDurationChip';
import { canMarkNoShowFromCheckInDate, noShowEligibilityTooltip } from '../../utils/noShowEligibility';
import { formatNoShowOtaMessage, getNoShowOtaState } from '../../utils/noShowOtaStatus';
import ChannelImage from '../common/ChannelImage';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import BookingAuditDrawer from './BookingAuditDrawer';
import { BookingDrawerCodeChip, DrawerDetailLink, DrawerGuestFieldLink } from './DrawerEntityLink';
import { getBookingDetailPath, enrichBookingGuestRef } from '../../utils/bookingDrawerLinks';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

const NoShowDrawer = ({ open, onClose, bookingId, onNoShowSuccess }) => {
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [markingNoShow, setMarkingNoShow] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [noShowReason, setNoShowReason] = useState('');
  const [markResult, setMarkResult] = useState(null);
  const drawerContainerRef = useRef(null);
  const theme = useTheme();
  
  // Initialize theme synchronously to prevent flash
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const parsedAuditBookingId = Number(booking?.id || booking?.booking_id || bookingId);
  const auditBookingId = Number.isInteger(parsedAuditBookingId) && parsedAuditBookingId > 0
    ? parsedAuditBookingId
    : null;

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (open && bookingId) {
      fetchBookingDetails();
    } else {
      setBooking(null);
      setError(null);
      setAuditDrawerOpen(false);
      setNoShowReason('');
      setMarkResult(null);
    }
  }, [open, bookingId]);

  const fetchBookingDetails = async () => {
    setError(null);
    try {
      const data = await getBookingDetails(bookingId);
      setBooking(enrichBookingGuestRef(data));
    } catch (err) {
      setError(err.message || 'Failed to load booking details');
    }
  };

  const handleMarkNoShow = async () => {
    if (!booking) return;
    
    setMarkingNoShow(true);
    try {
      const result = await markBookingNoShow(bookingId, noShowReason);
      setMarkResult(result);
      setConfirmDialogOpen(false);
      setMarkingNoShow(false);
      if (result.success) {
        if (result.marked && !result.needs_retry) {
          setError(null);
        }
        if (onNoShowSuccess) {
          onNoShowSuccess(result);
        }
      } else {
        setError(formatNoShowOtaMessage(result.error) || 'Failed to mark booking as no-show');
      }
      await fetchBookingDetails();
    } catch (err) {
      setConfirmDialogOpen(false);
      setError(
        formatNoShowOtaMessage(err.response?.data?.error || err.message)
        || 'Failed to mark booking as no-show'
      );
    } finally {
      setMarkingNoShow(false);
    }
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    // Prevent closing dialog while marking as no-show
    if (markingNoShow) return;
    setConfirmDialogOpen(false);
    setNoShowReason('');
  };

  // Helper functions for formatting dates and times
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD MMM YYYY');
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return '';
    return moment(dateTime).format('hh:mm A');
  };

  const noShowOta = useMemo(
    () => getNoShowOtaState(booking, markResult),
    [booking, markResult],
  );
  const stayCheckInDisplay = useMemo(() => resolveStayCheckInDisplay(booking), [booking]);
  const stayCheckOutDisplay = useMemo(() => resolveStayCheckOutDisplay(booking), [booking]);
  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);

  // Helper function for formatting currency
  const formatCurrency = (amount, currency = 'INR') => {
    if (amount === null || amount === undefined) return `${currency} 0.00`;
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return `${currency} 0.00`;
    return `${currency} ${numAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (!open) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            height: '100%',
            backgroundColor: isDark ? '#1e1e24' : '#f5f5f5',
          },
        }}
      >
        <Box ref={drawerContainerRef} sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          {/* Header - No Show with Icon */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NoShowIcon sx={{ color: '#ff9800', fontSize: 24 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                No Show
              </Typography>
              {booking && (
                <>
                  <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mx: 0.5 }}>
                    -
                  </Typography>
                  <BookingDrawerCodeChip
                    booking={booking}
                    bookingId={bookingId}
                    label={booking.code || `#${booking.id}`}
                    onNavigate={onClose}
                  />
                </>
              )}
            </Box>
            {canViewBookingAudit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAuditDrawerOpen(true)}
              disabled={!auditBookingId}
              sx={{
                borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
                color: CUSTOM_BORDER_COLOR,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1.5,
                py: 0.3,
                minWidth: 84,
                '&:hover': {
                  borderColor: CUSTOM_BORDER_COLOR,
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
                },
                '&.Mui-disabled': {
                  borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                  color: isDark ? drawerTheme.subtleText : '#999',
                },
              }}
            >
              Audit
            </Button>
            )}
          </Box>

          {/* Content - Scrollable with padding for fixed footer */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, pb: 10 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {!booking && !error && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Skeleton variant="rectangular" height={200} />
                <Skeleton variant="text" height={40} />
                <Skeleton variant="text" height={40} />
              </Box>
            )}

            {booking && (
              <>
                {/* Booking Information - Matching BookingDetailsDrawer Design */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                    border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                    borderRadius: '12px',
                    position: 'relative',
                  }}
                >
                  {/* Booking Source Image and Status Chip - Top Right */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    {/* Booking Source Image - Channel or PMS */}
                    {booking.channel ? (
                      <ChannelImage channel={booking.channel} size={24} />
                    ) : (
                      <ChannelImage isPMS={true} size={24} />
                    )}
                    
                    {/* Booking Status Chip */}
                    {booking.status && (
                      <Chip
                        label={booking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: (() => {
                            const statusLower = (booking.status || '').toLowerCase();
                            if (statusLower === 'confirmed') {
                              return isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                            } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                              return isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)';
                            } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                              return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                            } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                              return isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)';
                            } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                              return isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)';
                            }
                            return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                          })(),
                          color: (() => {
                            const statusLower = (booking.status || '').toLowerCase();
                            if (statusLower === 'confirmed') {
                              return isDark ? '#64b5f6' : '#1976d2';
                            } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                              return isDark ? '#81c784' : '#2e7d32';
                            } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                              return isDark ? '#bdbdbd' : '#616161';
                            } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                              return isDark ? '#ef5350' : '#c62828';
                            } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                              return isDark ? '#ffb74d' : '#e65100';
                            }
                            return isDark ? '#bdbdbd' : '#616161';
                          })(),
                          border: (() => {
                            const statusLower = (booking.status || '').toLowerCase();
                            if (statusLower === 'confirmed') {
                              return `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'}`;
                            } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                              return `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`;
                            } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                              return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                            } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                              return `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'}`;
                            } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                              return `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`;
                            }
                            return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                          })(),
                          backdropFilter: 'blur(10px)',
                          boxShadow: isDark 
                            ? '0 2px 8px rgba(0,0,0,0.3)' 
                            : '0 2px 8px rgba(0,0,0,0.1)',
                          textTransform: 'capitalize',
                          letterSpacing: '0.5px',
                        }}
                      />
                    )}
                    {isSameDayStay(booking) && (
                      <Chip
                        label="Hourly"
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                          color: isDark ? '#ffb74d' : '#ef6c00',
                          border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.45)' : 'rgba(239, 108, 0, 0.35)'}`,
                        }}
                      />
                    )}
                  </Box>
                  <Grid container spacing={1.5}>
                    {/* Primary Guest Information */}
                    {(() => {
                      // Get primary guest information
                      const primaryGuest = booking.guest || null;
                      const guestName = booking.guest_name || 
                        (primaryGuest ? [primaryGuest.title, primaryGuest.first_name, primaryGuest.middle_name, primaryGuest.last_name].filter(Boolean).join(' ') : null) ||
                        (primaryGuest ? primaryGuest.name : null);
                      const guestPhone = booking.guest_phone || booking.phone || 
                        (primaryGuest ? primaryGuest.phone : null);
                      const guestEmail = primaryGuest ? primaryGuest.email : null;
                      
                      if (!guestName && !guestPhone) return null;
                      
                      return (
                        <>
                          {/* Guest Name */}
                          {guestName && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PersonIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                  <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                    Guest Name:
                                  </Typography>
                                  <DrawerGuestFieldLink
                                    isDark={isDark}
                                    booking={booking}
                                    guest={primaryGuest}
                                    guestId={booking?.guest_id ?? primaryGuest?.id}
                                    variant="body2"
                                    onNavigate={onClose}
                                    sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                                  >
                                    {guestName}
                                  </DrawerGuestFieldLink>
                                </Box>
                              </Box>
                            </Grid>
                          )}

                          {/* Mobile Number */}
                          {guestPhone && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                  Mobile Number:
                                </Typography>
                                <DrawerGuestFieldLink
                                  isDark={isDark}
                                  booking={booking}
                                  guest={primaryGuest}
                                  guestId={booking?.guest_id ?? primaryGuest?.id}
                                  variant="body2"
                                  onNavigate={onClose}
                                  sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                                >
                                  {guestPhone}
                                </DrawerGuestFieldLink>
                              </Box>
                            </Grid>
                          )}

                          {/* Email */}
                          {guestEmail && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                                <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                                  Email:
                                </Typography>
                                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 500, fontSize: '0.85rem' }}>
                                  {guestEmail}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </>
                      );
                    })()}

                    {/* Check-in Information */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: '8px',
                          background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                          border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                          minHeight: booking.check_in_time ? 'auto' : '72px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckInIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                              Check-In
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                          {stayCheckInDisplay.date || '—'}
                        </Typography>
                        {stayCheckInDisplay.time ? (
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                            {stayCheckInDisplay.time}
                          </Typography>
                        ) : (
                          <Box sx={{ height: '16px' }} />
                        )}
                      </Box>
                      <ActualStayDateChips actual={actualCheckInDisplay} label="Actual check-in" isDark={isDark} />
                    </Grid>

                    {/* Check-out Information */}
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: '8px',
                          background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                          border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                          minHeight: (booking.check_out_time_display || booking.check_out_time) ? 'auto' : '72px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckOutIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                            <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                              Check-Out
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                          {stayCheckOutDisplay.date || '—'}
                        </Typography>
                        {stayCheckOutDisplay.time ? (
                          <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                            {stayCheckOutDisplay.time}
                          </Typography>
                        ) : (
                          <Box sx={{ height: '16px' }} />
                        )}
                      </Box>
                      <ActualStayDateChips actual={actualCheckOutDisplay} label="Actual check-out" isDark={isDark} />
                    </Grid>

                    {/* Nights / Hourly and Rooms */}
                    <Grid item xs={6}>
                      <StayDurationChip
                        booking={booking}
                        isDark={isDark}
                        accentColor={CUSTOM_BORDER_COLOR}
                        captionSx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Number of Rooms:
                        </Typography>
                        <Chip
                          icon={<HotelIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />}
                          label={booking.total_rooms || booking.rooms || 0}
                          size="small"
                          sx={{
                            backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                            color: CUSTOM_BORDER_COLOR,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Payment Information - Matching BookingDetailsDrawer Design */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    mb: 1.5,
                    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    borderRadius: '8px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <PaymentIcon sx={{ fontSize: 14, color: CUSTOM_BORDER_COLOR }} />
                    <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600 }}>
                      Payment
                    </Typography>
                  </Box>
                  
                  {/* Total Amount */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.85rem' }}>
                      {formatCurrency(booking.total_amount, booking.currency)}
                    </Typography>
                  </Box>

                  {/* Paid Amount */}
                  {booking.payment_summary && (
                    <>
                      <Divider sx={{ my: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Paid Amount
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? '#81c784' : '#2e7d32', fontSize: '0.85rem' }}>
                          {formatCurrency(booking.payment_summary.total_paid, booking.currency)}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {/* Remaining Balance */}
                  {booking.payment_summary && (
                    <>
                      <Divider sx={{ my: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }} />
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 1,
                          borderRadius: '6px',
                          backgroundColor: booking.payment_summary.remaining_balance > 0
                            ? (isDark ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)')
                            : (isDark ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)'),
                        }}
                      >
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600 }}>
                          Remaining
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            color: booking.payment_summary.remaining_balance > 0
                              ? (isDark ? '#ffb74d' : '#f57c00')
                              : (isDark ? '#81c784' : '#2e7d32'),
                          }}
                        >
                          {formatCurrency(booking.payment_summary.remaining_balance, booking.currency)}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Paper>

                {/* Show success message when payment is clear */}
                {booking.payment_summary && booking.payment_summary.remaining_balance <= 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      backgroundColor: isDark 
                        ? 'rgba(76, 175, 80, 0.1)' 
                        : 'rgba(76, 175, 80, 0.05)',
                      border: `2px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`,
                      borderRadius: '12px',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#fff', fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 700, 
                            color: isDark ? '#81c784' : '#2e7d32',
                            fontSize: '0.9rem',
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          Payment Clear
                          <Chip
                            label="Complete"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              backgroundColor: isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.15)',
                              color: isDark ? '#81c784' : '#2e7d32',
                              border: `1px solid ${isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(76, 175, 80, 0.3)'}`,
                            }}
                          />
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: isDark ? drawerTheme.subtleText : '#666',
                            fontSize: '0.8rem',
                            lineHeight: 1.5,
                            display: 'block',
                          }}
                        >
                          All payments have been collected for this booking.
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}

                {/* No Show Reason (Optional) */}
                <Card sx={{ mb: 2, backgroundColor: isDark ? drawerTheme.surface : '#ffffff' }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: isDark ? drawerTheme.text : '#333' }}>
                      Reason (Optional)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Enter reason for no-show..."
                      value={noShowReason}
                      onChange={(e) => setNoShowReason(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isDark ? '#1e1e24' : '#ffffff',
                          color: isDark ? drawerTheme.text : '#333',
                        },
                      }}
                    />
                  </CardContent>
                </Card>

                {noShowOta.needsRetry ? (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      No Show pending — {noShowOta.partnerLabel} did not accept
                    </Typography>
                    {noShowOta.roomsReleased
                      ? 'Rooms were released so they can be sold here. Mark no-show again to push it to '
                      : 'Mark no-show again to push it to '}
                    {noShowOta.partnerLabel}. Until then this booking stays No Show pending.
                  </Alert>
                ) : null}

                {noShowOta.otaSuccess || (noShowOta.isMarked && noShowOta.requiresOta) ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      No-show recorded on {noShowOta.partnerLabel}
                    </Typography>
                    {noShowOta.message || `OTA status: ${noShowOta.partnerLabel} accepted the no-show.`}
                  </Alert>
                ) : null}

                {noShowOta.requiresOta || noShowOta.ota ? (
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      borderRadius: '10px',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontWeight: 700, display: 'block', mb: 1 }}>
                      OTA status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={noShowOta.partnerLabel}
                        size="small"
                        sx={{ fontWeight: 700, height: 22 }}
                      />
                      <Chip
                        label={
                          noShowOta.otaSuccess
                            ? 'Accepted'
                            : noShowOta.needsRetry
                              ? 'Not accepted — retry'
                              : 'Not sent yet'
                        }
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          backgroundColor: noShowOta.otaSuccess
                            ? (isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.12)')
                            : noShowOta.needsRetry
                              ? (isDark ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)')
                              : (isDark ? 'rgba(255, 152, 0, 0.18)' : 'rgba(255, 152, 0, 0.1)'),
                          color: noShowOta.otaSuccess
                            ? (isDark ? '#81c784' : '#2e7d32')
                            : noShowOta.needsRetry
                              ? (isDark ? '#ef9a9a' : '#c62828')
                              : (isDark ? '#ffb74d' : '#e65100'),
                        }}
                      />
                    </Box>
                    {noShowOta.message ? (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: isDark ? drawerTheme.subtleText : '#666' }}>
                        {noShowOta.message}
                      </Typography>
                    ) : null}
                  </Paper>
                ) : null}

                {/* Warning Message */}
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {noShowOta.requiresOta
                    ? `This sends a no-show to ${noShowOta.partnerLabel}. If that OTA does not accept it, rooms are released here and the booking stays No Show pending until you retry.`
                    : 'Marking this booking as no-show will update the booking status and may trigger channel manager notifications.'}
                </Alert>
              </>
            )}
          </Box>

          {/* Footer - Fixed with No Show Button */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 1.5,
              borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
              backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
              zIndex: 10,
              boxShadow: isDark ? '0 -2px 8px rgba(0,0,0,0.3)' : '0 -2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onClose}
                  disabled={markingNoShow}
                  sx={{
                    borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                    color: isDark ? drawerTheme.text : '#666',
                    '&:hover': {
                      borderColor: CUSTOM_BORDER_COLOR,
                      backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                      color: CUSTOM_BORDER_COLOR,
                    },
                    '&:disabled': {
                      borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                      color: isDark ? drawerTheme.subtleText : '#999',
                    },
                  }}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                {(() => {
                  const alreadyMarked = noShowOta.isMarked && !noShowOta.needsRetry;
                  const canMarkNoShow =
                    booking &&
                    !alreadyMarked &&
                    canMarkNoShowFromCheckInDate(booking.check_in_date);
                  const isDisabled = markingNoShow || !booking || !canMarkNoShow;
                  const tooltipTitle =
                    alreadyMarked
                      ? 'This booking is already marked as no-show.'
                      : !canMarkNoShow && booking
                        ? noShowEligibilityTooltip(booking.check_in_date)
                        : '';
                  return (
                    <Tooltip title={tooltipTitle} placement="top" arrow>
                      <span style={{ width: '100%' }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handleOpenConfirmDialog}
                          disabled={isDisabled}
                          startIcon={<NoShowIcon />}
                          sx={{
                            backgroundColor: '#ff9800',
                            color: '#fff',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#f57c00',
                            },
                            '&:disabled': {
                              backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                              color: isDark ? drawerTheme.subtleText : '#999',
                            },
                          }}
                        >
                          {markingNoShow
                            ? 'Marking...'
                            : alreadyMarked
                              ? 'No Show marked'
                              : noShowOta.needsRetry
                                ? 'Retry No Show'
                                : canMarkNoShow
                                  ? 'No Show'
                                  : 'Not yet available'}
                        </Button>
                      </span>
                    </Tooltip>
                  );
                })()}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
      {canViewBookingAudit && (
        <BookingAuditDrawer
          open={auditDrawerOpen}
          onClose={() => setAuditDrawerOpen(false)}
          bookingId={auditBookingId}
          bookingCode={booking?.code || booking?.booking_code}
          isDark={isDark}
        />
      )}

      {/* Confirmation Dialog - Inside Drawer */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        disableEscapeKeyDown={markingNoShow}
        container={drawerContainerRef.current || undefined}
        sx={{
          position: 'absolute',
          zIndex: 1400,
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
            borderRadius: 3,
            minWidth: 400,
            maxWidth: 500,
            margin: 2,
            position: 'absolute',
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            position: 'absolute',
          },
        }}
      >
        <DialogTitle sx={{ color: isDark ? drawerTheme.text : '#333' }}>
          Confirm No Show
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', mb: 2 }}>
            {noShowOta.needsRetry
              ? `Retry no-show on ${noShowOta.partnerLabel}? Rooms stay released until the OTA accepts.`
              : noShowOta.requiresOta
                ? `This will push no-show to ${noShowOta.partnerLabel}. If it fails, rooms are released and the booking stays pending.`
                : 'Are you sure you want to mark this booking as no-show?'}
          </Typography>
          {booking && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.75rem', mb: 1 }}>
                Booking:{' '}
                <DrawerDetailLink
                  to={getBookingDetailPath(booking)}
                  variant="body2"
                  onNavigate={onClose}
                  sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 700, fontSize: '0.75rem', display: 'inline' }}
                >
                  {booking.code || `#${booking.id}`}
                </DrawerDetailLink>
              </Typography>
              {booking.guest && (
                <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.75rem' }}>
                  Guest:{' '}
                  <DrawerGuestFieldLink
                    isDark={isDark}
                    booking={booking}
                    guest={booking.guest}
                    guestId={booking?.guest_id ?? booking?.guest?.id}
                    variant="body2"
                    onNavigate={onClose}
                    sx={{ fontWeight: 700, fontSize: '0.75rem', display: 'inline' }}
                  >
                    {booking.guest.name || `${booking.guest.first_name || ''} ${booking.guest.last_name || ''}`.trim() || 'N/A'}
                  </DrawerGuestFieldLink>
                </Typography>
              )}
            </Box>
          )}
          {noShowReason && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: isDark ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)', borderRadius: '8px' }}>
              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                Reason:
              </Typography>
              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.text : '#333', fontSize: '0.85rem' }}>
                {noShowReason}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={markingNoShow}>
            Cancel
          </Button>
          <Button
            onClick={handleMarkNoShow}
            variant="contained"
            disabled={
              markingNoShow ||
              !booking ||
              (noShowOta.isMarked && !noShowOta.needsRetry) ||
              !canMarkNoShowFromCheckInDate(booking.check_in_date)
            }
            startIcon={markingNoShow ? <CircularProgress size={16} /> : <NoShowIcon />}
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
              '&:disabled': {
                backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            {markingNoShow ? 'Marking...' : noShowOta.needsRetry ? 'Retry No Show' : 'Confirm No Show'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NoShowDrawer;
